import React from 'react';
import { Line } from 'react-chartjs-2';

import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns'; // needed for the TimeScale

import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
} from 'chart.js';

import { drawCurrentDateLine } from '@UI-Elements/ChartPlugins/CurrentDateLinePlugin';
import { drawLinePlugin } from '@UI-Elements/ChartPlugins/DrawLinePlugin';
import { handleMouseDown, handleMouseMove, handleMouseUp } from '@UI-Elements/ChartPlugins/ChartUtils';
import { IndexRoutes } from './HistoricalUtils';

ChartJS.register(
  CategoryScale,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
);

const chartAreaBorder = {
  id: 'chartAreaBorder',
  borderColor: 'rgba(0, 0, 0, 0.1)',
  borderWidth: 1,
  borderDashOffset: 1,
  beforeDraw: (chart: any) => {
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = chartAreaBorder.borderColor;
    ctx.lineWidth = chartAreaBorder.borderWidth;
    ctx.lineDashOffset = chartAreaBorder.borderDashOffset;
    ctx.strokeRect(chartArea.left, chartArea.top, chartArea.width, chartArea.height);
    ctx.restore();
  },
};

const getOptions = (
  onDataClick: (_dataIndex: number, _x: number) => void,
  minIndex: number,
  xPoint: number,
  currency: string,
  isDrawPrice: boolean | undefined,
): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: false,

    title: { display: false },
    tooltip: { mode: 'index', intersect: false },
    drawCurrentDateLine: true,
    drawLinePlugin: { minIndex, xPoint },
    zoom: {
      zoom: {
        wheel: {
          enabled: true,
        },
        pinch: {
          enabled: true,
        },
        mode: 'xy',
      },
      pan: {
        enabled: false,
        mode: 'xy',
      },
    },
  } as any,
  onHover: (event: any) => {
    if (event.type === 'click') {
      const activeElements = event.chart.tooltip?.getActiveElements();
      if (activeElements?.length) {
        const {
          index: dataIndex,
          element: { x },
        } = activeElements[0];
        const topY = event.chart.scales.y.top;
        const bottomY = event.chart.scales.y.bottom;
        const clickedData = { dataIndex, topY, bottomY, x };
        event.chart.clicked = clickedData;
        onDataClick(clickedData.dataIndex, x);
      }
    }
  },
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'month' as const,
        displayFormats: { month: 'MM/yy' },
      },
      title: {
        display: false,
        text: 'Month',
      },
      ticks: { display: true, autoSkip: true },
      grid: { display: true },
    },
    y: {
      beginAtZero: false,
      offset: true,
      title: {
        display: true,
        text: isDrawPrice ? `Material Price (${currency})` : 'Contago / Backwardation percentage (%)',
      },
      grid: {
        color: (context) => (context.tick.value === 0 ? '#AAA' : 'rgba(0, 0, 0, 0.1)'),
        lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
      },
    },
  },
  elements: { line: { tension: 0.2 }, point: { radius: 0 } },
});

const getData = (materialRoutes: any[], materialColor: string, drawPrice?: boolean): ChartData<'line'> => ({
  labels: materialRoutes.map((r) => r.date),
  datasets: drawPrice
    ? [
        {
          label: `Material Price`,
          data: materialRoutes.map((d) => ({
            x: new Date(d.date).getTime(),
            y: d.price,
          })),
          borderColor: materialColor,
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
          fill: false,
          borderWidth: 4,
        },
        {
          label: 'Future price',
          data: materialRoutes.map((d) => ({
            x: new Date(d.date).getTime(),
            y: d.future,
          })),
          borderColor: '#19ceb8',
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
          fill: false,
          borderWidth: 2,
        },
      ]
    : [
        {
          label: 'Future compare',
          data: materialRoutes.map(({ date, price, future }) => ({
            x: new Date(date).getTime(),
            y: ((future - price) / price) * 100,
          })),
          borderColor: '#19ceb8',
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
          fill: false,
          borderWidth: 2,
        },
      ],
});

interface Props {
  indexRoutesData: IndexRoutes[];
  onDataClick: (_priceData: IndexRoutes, _x: number) => void;
  materialColor: string;
  currency: string;
  lineXcoord: number;
  isDrawPrice?: boolean;
}
export const HistoricalChart: React.FC<Props> = ({
  indexRoutesData,
  onDataClick,
  materialColor,
  currency,
  lineXcoord,
  isDrawPrice,
}) => {
  const chartRef = React.useRef();

  React.useEffect(() => {
    //@ts-expect-error-type-not-match
    const chart = chartRef.current as ChartJS<'line'>;
    if (chart?.canvas) {
      chart.canvas.addEventListener('mousedown', handleMouseDown(chart));
      chart.canvas.addEventListener('mousemove', handleMouseMove(chart));
      chart.canvas.addEventListener('mouseup', handleMouseUp(chart));
    }

    return () => {
      if (chart?.canvas) {
        chart.canvas.removeEventListener('mousemove', handleMouseMove(chart));
        chart.canvas.removeEventListener('mousedown', handleMouseDown(chart));
        chart.canvas.removeEventListener('mouseup', handleMouseUp(chart));
      }
    };
  }, []);

  const innerClick = (dIndex: number, x: number) => {
    onDataClick(indexRoutesData[dIndex], x);
  };

  return (
    <div className="flex-1 min-h-[300px]">
      <Line
        options={getOptions(innerClick, indexRoutesData.length - 12, lineXcoord, currency, isDrawPrice)}
        data={getData(indexRoutesData, materialColor, isDrawPrice)}
        plugins={[drawLinePlugin, chartAreaBorder, drawCurrentDateLine]}
        ref={chartRef}
      />
    </div>
  );
};
